import { usePollingInterval } from 'src/hooks/usePollingInterval'
import {
  makeStyles,
  styled,
  Activity,
  ActivityTimeline,
  DialogStackContext,
  Grid,
  Link,
  NoResults,
  Spinner,
  useClientOptions,
  useInfiniteScroll
} from '@paintscout/ui'
import type { MuiTheme as Theme, ActivityProps } from '@paintscout/ui'
import { useActivitiesForDocQuery, useNewActivitiesForDocQuery } from '@paintscout/api'
import { QuoteContext, QuoteRevisionDialog, useQuote, ViewPaymentDialog } from '@paintscout/react-quote'
import { getObjectLabels } from '@paintscout/util/builder'
import uniqBy from 'lodash/uniqBy'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import BaseSidebarPage from '../BaseSidebarPage'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative'
  },
  spinner: {
    marginTop: theme.spacing()
  },
  timeline: {
    paddingTop: theme.spacing()
  }
}))

const ACTIVITY_LIMIT = 20
const dealActivityTypes = ['deal-created', 'deal-assigned', 'deal-stage-changed', 'lead-form-submission']
const eventActivityTypes = [
  'event',
  'event-created',
  'event-cancelled',
  'event-assigned',
  'event-email-sent',
  'event-progress-updated'
]
const taskActivityTypes = ['task-created', 'task-assigned', 'task-completed']

export function getDealActivityLink(activity: ActivityProps['activity']) {
  const details = activity?.details
  const { activityType } = details || {}

  if (!details?.deal?.id) {
    return null
  }

  if (eventActivityTypes.includes(activityType)) {
    return `/deals/edit/${details.deal.id}?eventTab=events&viewTab=details`
  }

  if (taskActivityTypes.includes(activityType)) {
    return `/deals/edit/${details.deal.id}?eventTab=tasks&viewTab=details`
  }

  if (dealActivityTypes.includes(activityType)) {
    return `/deals/edit/${details.deal.id}`
  }

  return null
}

function QuoteActivity() {
  const classes = useStyles({})

  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { quote } = useContext(QuoteContext)
  const ref = useRef(null)
  const [pollTimestamp, setPollTimestamp] = useState(Date.now())

  const { loading, data, error, fetchMore, updateQuery } = useActivitiesForDocQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: quote._id,
      limit: ACTIVITY_LIMIT
    },
    onError: useCallback((e) => {
      Sentry.captureException(e, { tags: { quoteId: quote._id } })
    }, [])
  })

  const activities = ((data && data.activitiesForDoc && data.activitiesForDoc.activities) || []).filter(
    (activity) => !!activity?.details?.activityType && activity.details.activityType !== 'message'
  )

  const hasMore = !!data?.activitiesForDoc?.bookmark

  useNewActivitiesForDocQuery({
    pollInterval: usePollingInterval(45000),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: loading,
    variables: useMemo(() => {
      return {
        id: quote._id,
        since: pollTimestamp
      }
    }, [pollTimestamp]),
    onCompleted: useCallback(
      (data) => {
        if (data?.newActivitiesForDoc?.length > 0) {
          setPollTimestamp(Date.now())
          updateQuery((prev) => {
            const newActivities = data.newActivitiesForDoc.filter(
              (activity) => !prev.activitiesForDoc.activities.find(({ _id }) => _id === activity._id)
            )
            const updatedActivities = data.newActivitiesForDoc.filter(
              (activity) => !!prev.activitiesForDoc.activities.find(({ _id }) => _id === activity._id)
            )

            return {
              ...prev,
              activitiesForDoc: {
                ...prev.activitiesForDoc,
                total_rows: prev.activitiesForDoc.total_rows + data.newActivitiesForDoc.length,
                activities: [
                  ...newActivities,
                  ...prev.activitiesForDoc.activities.map((activity) => {
                    const updatedActivity = updatedActivities.find(({ _id }) => _id === activity._id)
                    return updatedActivity || activity
                  })
                ].filter((activity) => !!activity?.details?.activityType)
              }
            }
          })
        }
      },
      [updateQuery]
    )
  })

  useInfiniteScroll({
    ref,
    disabled: !hasMore || loading,
    scrollPercentage: 75,
    onLoadMore: () => {
      fetchMore({
        variables: {
          id: quote._id,
          bookmark: data.activitiesForDoc.bookmark,
          limit: ACTIVITY_LIMIT
        },
        updateQuery(prev, { fetchMoreResult }) {
          if (!prev) {
            return fetchMoreResult
          }

          if (!fetchMoreResult.activitiesForDoc) {
            return prev
          }

          return {
            ...prev,
            ...fetchMoreResult,
            activitiesForDoc: {
              ...prev.activitiesForDoc,
              ...fetchMoreResult.activitiesForDoc,

              // duplicates will come in if newActivitiesForDoc has returned any new docs, so we need to filter them out
              activities: uniqBy(
                [...prev.activitiesForDoc.activities, ...fetchMoreResult.activitiesForDoc.activities],
                '_id'
              )
            }
          }
        }
      })
    }
  })

  if (error) {
    return <StyledNoResults message={'An unexpected error occurred'} />
  }

  if (!loading && activities.length === 0) {
    return <StyledNoResults message={`Changes made to your ${objectLabels.quote.value} will show here`} />
  }

  return (
    <div ref={ref} className={classes.root}>
      <BaseSidebarPage>
        <Grid container spacing={0} className={classes.timeline}>
          <Grid item xs={12}>
            <ActivityTimeline
              activities={activities}
              loading={loading && activities.length === 0}
              shimRowAmount={10}
              showDocTypeDetails={false}
              showContent
              ActivityComponent={ActivityComponent}
            />
            {hasMore && loading && <Spinner className={classes.spinner} />}
          </Grid>
        </Grid>
      </BaseSidebarPage>
    </div>
  )
}

const StyledNoResults = styled(NoResults)(({ theme }: { theme: Theme }) => ({
  marginTop: theme.spacing(4)
}))

const useActivityStyles = makeStyles(() => ({
  root: {
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
      // move focus outline a bit to the left
      '& $content:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 4,
        bottom: 0,
        top: 0,
        outline: '-webkit-focus-ring-color auto 5px'
      }
    }
  },
  content: {
    position: 'relative'
  },
  link: {
    color: 'inherit',
    textDecoration: 'none',
    '&:visited, &:active, &:focus': {
      color: 'inherit'
    }
  }
}))

function ActivityComponent(props: ActivityProps) {
  const classes = useActivityStyles({})

  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { quote } = useQuote()

  function handleUrlClick(url: string) {
    openDialog(QuoteRevisionDialog, {
      activity: props.activity,
      url,
      onConfirm: dismissDialog
    })
  }

  function handlePaymentClick() {
    openDialog(ViewPaymentDialog, {
      quote,
      activityId: props.activity._id,
      onClose: dismissDialog
    })
  }

  if (['sent', 'restored', 'accepted'].includes(props.activity.details.activityType)) {
    return (
      <button
        className={classes.root}
        onClick={(ev) => {
          ev.currentTarget?.blur()
          handleUrlClick(props.activity.details.url)
        }}
      >
        <Activity {...props} classes={{ content: classes.content }} />
      </button>
    )
  }

  if (
    props.activity.details.activityType === 'payment' &&
    ['stripe', 'stripeW1d'].includes((props.activity.details as any).provider)
  ) {
    return (
      <button onClick={handlePaymentClick}>
        <Activity {...props} />
      </button>
    )
  }

  const dealLink = getDealActivityLink(props.activity)
  if (dealLink) {
    return (
      <Link to={dealLink} classes={{ root: classes.link }}>
        <Activity {...props} />
      </Link>
    )
  }

  return <Activity {...props} />
}

export default QuoteActivity
